import {
  attachClosestEdge,
  Edge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import invariant from 'tiny-invariant'

interface LeftNavReorderableItemWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  draggableItemType: string // i.e. 'group', 'child-table', etc.
  item: {
    id: string
    name: string
    order: number | null
    icon?: ReactNode
  }
  canDragAndDrop?: boolean
}
/**
 * Presentational component to give a single item in the left nav drag and drop capabilities
 *
 * When the item is being dragged, it will show a preview.
 * When the item is the drop target of another item being dragged, it will show a drop indicator.
 * See example usage in TableSettingsLeftNavReorderableItem.tsx
 */
export function LeftNavReorderableItemWrapper({
  children,
  draggableItemType,
  item,
  canDragAndDrop = true,
  ...rest
}: LeftNavReorderableItemWrapperProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null)

  // Sets up drag and drop for a single item in the left nav
  // An item is both draggable and a drop target
  useEffect(() => {
    if (!canDragAndDrop) {
      return
    }
    const row = ref.current
    invariant(row)

    const draggableSetup = draggable({
      element: row,
      getInitialData() {
        return {
          type: draggableItemType,
          id: item.id,
          order: item.order,
        }
      },
      onGenerateDragPreview({ nativeSetDragImage }) {
        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: pointerOutsideOfPreview({
            x: '16px',
            y: '8px',
          }),
          render({ container }) {
            const root = createRoot(container)
            root.render(
              <div className="flex items-center gap-2 bg-gray-12 p-2 text-white">
                <div className="flex min-w-0 grow items-center gap-2 overflow-hidden">
                  {item.icon !== undefined && item.icon}
                  <div className="truncate">{item.name}</div>
                </div>
              </div>,
            )
            return () => root.unmount()
          },
        })
      },
    })

    const dropSetup = dropTargetForElements({
      element: row,
      getData({ input, element }) {
        const data = {
          id: item.id,
          order: item.order,
        }
        return attachClosestEdge(data, {
          input,
          element,
          allowedEdges: ['top', 'bottom'],
        })
      },
      onDrag(args) {
        if (args.source.data.id !== item.id) {
          setClosestEdge(extractClosestEdge(args.self.data))
        }
      },
      onDragLeave() {
        setClosestEdge(null)
      },
      onDrop() {
        setClosestEdge(null)
      },
    })

    // Clean up the drag and drop setup when the component unmounts
    return combine(draggableSetup, dropSetup)
  }, [canDragAndDrop, draggableItemType, item])

  return (
    <div ref={ref} className="relative" {...rest}>
      {closestEdge === 'top' && (
        <div className="pointer-events-none absolute -top-0.5 end-0 start-0 z-30 h-0.5 bg-blue-9" />
      )}
      {children}
      {closestEdge === 'bottom' && (
        <div className="pointer-events-none absolute -bottom-0.5 end-0 start-0 z-30 h-0.5 bg-blue-9" />
      )}
    </div>
  )
}
